import { LogOut, User, LogIn, MessageCircle, Send, Shield, Brain, Menu, X, BookOpen, Radar, FileWarning } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import ScamflagWordmark from './ScamflagWordmark';

type NavbarProps = {
  onNavigate: (page: 'home' | 'report-scam' | 'report-scammer' | 'auth' | 'profile' | 'admin' | 'scam-check' | 'ai-posts' | 'blog') => void;
  currentPage: string;
};

export default function Navbar({ onNavigate, currentPage }: NavbarProps) {
  const { user, profile, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const checkAdminStatus = async () => {
    const { data } = await supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', user!.id)
      .maybeSingle();
    setIsAdmin(!!data);
  };

  const handleNavigation = (page: any) => {
    onNavigate(page);
    setIsMenuOpen(false);
  };

  const pill = (page: string, active: boolean) =>
    active
      ? 'text-white font-semibold'
      : 'text-white/70 hover:bg-white/10 hover:text-white';

  return (
    <nav className="backdrop-blur-md border-b border-white/10 sticky top-0 z-50" style={{ backgroundColor: '#1A1A1A' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-14">
          <button
            onClick={() => handleNavigation('home')}
            className="flex items-center hover:opacity-90 transition flex-shrink-0"
          >
            <ScamflagWordmark iconClassName="w-8 h-8" wordmarkClassName="text-lg" />
          </button>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className="lg:hidden p-1.5 text-white/70 hover:bg-white/10 hover:text-white rounded-md transition"
          >
            {isMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>

          <div className="hidden lg:flex items-center gap-1">
            <button onClick={() => handleNavigation('scam-check')} className={`sf-nav-pill ${pill('scam-check', currentPage === 'scam-check')}`}>
              <Radar className="w-3.5 h-3.5 inline mr-1" />Is it Scam?
            </button>
            <button onClick={() => handleNavigation('ai-posts')} className={`sf-nav-pill ${pill('ai-posts', currentPage === 'ai-posts')}`}>
              <FileWarning className="w-3.5 h-3.5 inline mr-1" />Scam Checks
            </button>
            <button onClick={() => handleNavigation('blog')} className={`sf-nav-pill ${pill('blog', currentPage === 'blog')}`}>
              <BookOpen className="w-3.5 h-3.5 inline mr-1" />Blog
            </button>
            <button
              onClick={() => user ? handleNavigation('report-scam') : handleNavigation('auth')}
              className={`sf-nav-pill ${currentPage === 'report-scam' ? 'text-white font-semibold' : 'text-flag-400 hover:bg-flag-500/10'}`}
            >
              Report Scam
            </button>
            <button
              onClick={() => user ? handleNavigation('report-scammer') : handleNavigation('auth')}
              className={`sf-nav-pill ${currentPage === 'report-scammer' ? 'text-white font-semibold' : 'text-flag-400 hover:bg-flag-500/10'}`}
            >
              Report Scammer
            </button>

            <div className="flex items-center gap-0.5 ml-2 pl-2 border-l border-white/20">
              <a href="https://t.me/scamflag" target="_blank" rel="noopener noreferrer" className="p-1.5 text-white/60 hover:text-white rounded-md transition" title="Telegram">
                <Send className="w-4 h-4" />
              </a>
              <a href="https://tiktok.com/@scamflag" target="_blank" rel="noopener noreferrer" className="p-1.5 text-white/60 hover:text-white rounded-md transition" title="TikTok">
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>

            {user ? (
              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-white/20">
                {isAdmin && (
                  <button onClick={() => handleNavigation('admin')} className={`sf-nav-pill ${pill('admin', currentPage === 'admin')}`}>
                    <Shield className="w-3.5 h-3.5 inline mr-1" /><span className="hidden sm:inline">Admin</span>
                  </button>
                )}
                <button onClick={() => handleNavigation('profile')} className={`sf-nav-pill ${pill('profile', currentPage === 'profile')}`}>
                  <User className="w-3.5 h-3.5 inline mr-1" /><span className="hidden sm:inline">{profile?.username}</span>
                </button>
                <button onClick={signOut} className="p-1.5 text-white/60 hover:text-white rounded-md transition" title="Sign Out">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleNavigation('auth')}
                className="ml-2 pl-2 border-l border-white/20 flex items-center gap-1 sf-nav-pill text-white bg-white/15 hover:bg-white/25"
              >
                <LogIn className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sign In</span>
              </button>
            )}
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden border-t border-white/10 py-2 space-y-1">
            <button onClick={() => handleNavigation('scam-check')} className={`w-full text-left sf-nav-pill ${pill('scam-check', currentPage === 'scam-check')}`}>
              <Radar className="w-3.5 h-3.5 inline mr-1" />Is it Scam?
            </button>
            <button onClick={() => handleNavigation('ai-posts')} className={`w-full text-left sf-nav-pill ${pill('ai-posts', currentPage === 'ai-posts')}`}>
              <FileWarning className="w-3.5 h-3.5 inline mr-1" />Scam Checks
            </button>
            <button onClick={() => handleNavigation('blog')} className={`w-full text-left sf-nav-pill ${pill('blog', currentPage === 'blog')}`}>
              <BookOpen className="w-3.5 h-3.5 inline mr-1" />Blog
            </button>
            <button onClick={() => user ? handleNavigation('report-scam') : handleNavigation('auth')} className={`w-full text-left sf-nav-pill ${currentPage === 'report-scam' ? 'text-white font-semibold' : 'text-flag-400 hover:bg-flag-500/10'}`}>
              Report Scam
            </button>
            <button onClick={() => user ? handleNavigation('report-scammer') : handleNavigation('auth')} className={`w-full text-left sf-nav-pill ${currentPage === 'report-scammer' ? 'text-white font-semibold' : 'text-flag-400 hover:bg-flag-500/10'}`}>
              Report Scammer
            </button>
            <div className="border-t border-white/10 pt-1.5 flex gap-2">
              <a href="https://t.me/scamflag" target="_blank" rel="noopener noreferrer" className="sf-nav-pill text-white/70"><Send className="w-3.5 h-3.5 inline mr-1" />Telegram</a>
              <a href="https://tiktok.com/@scamflag" target="_blank" rel="noopener noreferrer" className="sf-nav-pill text-white/70"><MessageCircle className="w-3.5 h-3.5 inline mr-1" />TikTok</a>
            </div>
            <div className="border-t border-white/10 pt-1.5">
              {user ? (
                <div className="space-y-1">
                  {isAdmin && <button onClick={() => handleNavigation('admin')} className={`w-full text-left sf-nav-pill ${pill('admin', currentPage === 'admin')}`}><Shield className="w-3.5 h-3.5 inline mr-1" />Admin</button>}
                  <button onClick={() => handleNavigation('profile')} className={`w-full text-left sf-nav-pill ${pill('profile', currentPage === 'profile')}`}><User className="w-3.5 h-3.5 inline mr-1" />{profile?.username}</button>
                  <button onClick={signOut} className="w-full text-left sf-nav-pill text-white/70"><LogOut className="w-3.5 h-3.5 inline mr-1" />Sign Out</button>
                </div>
              ) : (
                <button onClick={() => handleNavigation('auth')} className="w-full sf-nav-pill text-white bg-white/15 hover:bg-white/25"><LogIn className="w-3.5 h-3.5 inline mr-1" />Sign In</button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
